// ClinicOS EHR — Voice Recorder Component
// Uses Web Speech API for real-time Spanish STT + Claude AI SOAP generation

import { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Loader2, Wand2, StopCircle, MicOff, Trash2 } from 'lucide-react';

type RecordingState = 'idle' | 'recording' | 'generating';

interface Props {
    encounterId: string;
    disabled?: boolean;
    onSoapDraft?: (draft: {
        subjective: string;
        objective: string;
        assessment: string;
        plan: string;
        diagnoses?: Array<{ icd10Code: string; description: string; type: string }>;
        medications?: Array<{ name: string; dose: string; frequency: string; duration: string; instructions: string }>;
        followUpDays?: number;
    }) => void;
}

export function VoiceRecorder({ encounterId, disabled, onSoapDraft }: Props) {
    const [state, setState] = useState<RecordingState>('idle');
    const [transcript, setTranscript] = useState('');
    const [interimText, setInterimText] = useState('');
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [soapModel, setSoapModel] = useState('');

    const recognitionRef = useRef<any>(null);
    const timerRef = useRef<number | null>(null);
    const finalTranscriptRef = useRef('');

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (recognitionRef.current) {
                try { recognitionRef.current.stop(); } catch { }
            }
        };
    }, []);

    const startRecording = useCallback(() => {
        setError(null);
        setTranscript('');
        setInterimText('');
        setDuration(0);
        finalTranscriptRef.current = '';

        // Check for Web Speech API support
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setError('Tu navegador no soporta reconocimiento de voz. Usa Chrome o Edge.');
            return;
        }

        try {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'es-CO'; // Colombian Spanish
            recognition.maxAlternatives = 1;

            recognition.onresult = (event: any) => {
                let interim = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const result = event.results[i];
                    if (result.isFinal) {
                        finalTranscriptRef.current += result[0].transcript + ' ';
                        setTranscript(finalTranscriptRef.current.trim());
                    } else {
                        interim += result[0].transcript;
                    }
                }
                setInterimText(interim);
            };

            recognition.onerror = (event: any) => {
                if (event.error === 'no-speech') return; // Ignore silence
                if (event.error === 'aborted') return;
                setError(`Error de micrófono: ${event.error}`);
                stopRecording();
            };

            recognition.onend = () => {
                // Auto-restart if still in recording state
                if (recognitionRef.current && state === 'recording') {
                    try { recognition.start(); } catch { }
                }
            };

            recognition.start();
            recognitionRef.current = recognition;
            setState('recording');

            // Duration timer
            const startTime = Date.now();
            timerRef.current = window.setInterval(() => {
                setDuration(Math.floor((Date.now() - startTime) / 1000));
            }, 1000);
        } catch {
            setError('No se pudo acceder al micrófono. Verifique los permisos.');
        }
    }, [state]);

    const stopRecording = useCallback(() => {
        if (recognitionRef.current) {
            try { recognitionRef.current.stop(); } catch { }
            recognitionRef.current = null;
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setInterimText('');
        setState('idle');
    }, []);

    const clearTranscript = useCallback(() => {
        setTranscript('');
        finalTranscriptRef.current = '';
        setInterimText('');
        setSoapModel('');
    }, []);

    const generateSoap = useCallback(async () => {
        const text = transcript.trim();
        if (!text) {
            setError('No hay transcripción para generar la nota SOAP');
            return;
        }

        setState('generating');
        setError(null);

        try {
            const res = await fetch('/api/voice/transcribe-to-soap?autoApply=true', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ encounterId, transcript: text, language: 'es' }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({ error: 'Error del servidor' }));
                throw new Error(err.error || `HTTP ${res.status}`);
            }

            const json = await res.json();
            const draft = json.data?.draft;
            setSoapModel(json.data?.model || 'unknown');

            if (draft && onSoapDraft) {
                onSoapDraft(draft);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al generar nota SOAP');
        } finally {
            setState('idle');
        }
    }, [transcript, encounterId, onSoapDraft]);

    const formatDuration = (s: number) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${String(sec).padStart(2, '0')}`;
    };

    if (disabled) return null;

    return (
        <div className="card" style={{
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-4)',
            border: state === 'recording' ? '2px solid var(--color-danger, #ef4444)' : undefined,
        }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (transcript || interimText) ? 'var(--space-3)' : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    {state === 'idle' && !transcript && (
                        <button className="btn btn-secondary" onClick={startRecording}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Mic size={16} /> Grabar consulta
                        </button>
                    )}
                    {state === 'idle' && transcript && (
                        <>
                            <button className="btn btn-secondary" onClick={startRecording}
                                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Mic size={16} /> Continuar
                            </button>
                            <button className="btn btn-icon btn-sm" onClick={clearTranscript} title="Borrar transcripción">
                                <Trash2 size={14} />
                            </button>
                        </>
                    )}
                    {state === 'recording' && (
                        <>
                            <button className="btn" onClick={stopRecording}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--color-danger, #ef4444)', color: '#fff', border: 'none' }}>
                                <StopCircle size={16} /> Detener
                            </button>
                            <span style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 'var(--text-sm)', color: 'var(--color-danger, #ef4444)',
                                fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                            }}>
                                <span style={{
                                    width: 8, height: 8, borderRadius: '50%',
                                    background: 'var(--color-danger, #ef4444)',
                                    animation: 'voicePulse 1s ease-in-out infinite',
                                }} />
                                REC {formatDuration(duration)}
                            </span>
                        </>
                    )}
                    {state === 'generating' && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-primary)' }}>
                            <Loader2 size={16} style={{ animation: 'voiceSpin 1s linear infinite' }} />
                            Generando nota SOAP con IA...
                        </span>
                    )}
                </div>

                {transcript && state === 'idle' && (
                    <button className="btn btn-primary" onClick={generateSoap}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Wand2 size={16} /> Generar SOAP con IA
                    </button>
                )}
            </div>

            {/* Transcript Display */}
            {(transcript || interimText) && (
                <div style={{
                    background: 'var(--color-bg)', borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-3)', maxHeight: 180, overflowY: 'auto',
                    fontSize: 'var(--text-sm)', lineHeight: 1.7,
                }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                        <span>📝 Transcripción en vivo:</span>
                        {soapModel && <span style={{ color: 'var(--color-success)' }}>✅ SOAP generado ({soapModel})</span>}
                    </div>
                    <span>{transcript}</span>
                    {interimText && (
                        <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                            {' '}{interimText}
                        </span>
                    )}
                </div>
            )}

            {/* Hint */}
            {!transcript && !interimText && state === 'idle' && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>
                    <MicOff size={12} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
                    Grabe la consulta y genere la nota SOAP automáticamente con IA
                </div>
            )}

            {/* Error */}
            {error && (
                <div style={{
                    marginTop: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)',
                    background: '#fff5f5', color: 'var(--color-danger, #ef4444)',
                    borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                }}>
                    {error}
                </div>
            )}

            <style>{`
                @keyframes voicePulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.3; }
                }
                @keyframes voiceSpin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
